"use client";

import { AuthSignInForm, Button } from "@prance/ui";
import { Loader2 } from "lucide-react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { AuthShell } from "~/components/auth/AuthShell";
import { INPUT_CLASS_MD } from "~/lib/core/form-classes";

type Stage = "login" | "new_password";

// prance-idリポジトリ docs/decision-log.md ADR-014(2026-09-01追記でpraxiも対象に追加)。
// proomi/prysm/proovaと同じ製品間SSO(カスタム認証チケット方式)を有効化する。
const SSO_SILENT_LOGIN_ENABLED = true;

/**
 * ログイン画面(PranceIdClientPool・praxi専用App Clientでの認証)。
 * レイアウトはproomi/prysm/proovaと同じ`AuthShell`(ブランドパネル+フォームの
 * 2カラム)を踏襲する(design.md §3.7)。サインインフォーム本体は`@prance/ui`の
 * `AuthSignInForm`(docs/shared-ui-library.md §3.16、2026-10-12追加)へ移行済み
 * (prysm/faqと同じ移行。new_password_required/forgot_password等の派生フローは
 * 対象外のため独自実装を維持)。製品間SSO(ADR-014)は他3製品と同様に有効。
 */
export default function LoginPage() {
  const t = useTranslations("AuthPage");
  const router = useRouter();

  const [stage, setStage] = useState<Stage>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pendingEmail, setPendingEmail] = useState("");
  const [pendingSession, setPendingSession] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // prance-idリポジトリ docs/decision-log.md ADR-014(製品間SSO)。マウント時に
  // 一度だけ`prance_id_sso_ticket`Cookieの有無を確認し、有効なら無言でログインを
  // 完了させる。チェック完了までフォームを表示しない(表示→即消滅のちらつきを防ぐ)。
  const [checkingSso, setCheckingSso] = useState(true);

  useEffect(() => {
    // docs/decision-log.md ADR-014(2026-10-12再訂正)。ログアウト直後の抑制判定は
    // サーバー側(/api/auth/silent-login内のshouldSkipSilentLogin、@prance/auth)の
    // Cookie判定で行うため、ここではクライアント側で何もチェックせず常にsilent-loginを
    // 呼ぶだけでよい(旧: クライアント側sessionStorageの1回限りフラグは、この
    // マウント時チェック自身がフラグを消費してしまい、その後の手動リロードに無防備という
    // 競合バグがあったため撤廃、SignOutButton.tsx参照)。
    if (!SSO_SILENT_LOGIN_ENABLED) {
      setCheckingSso(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch("/api/auth/silent-login", { method: "POST" });
        if (cancelled) return;
        if (res.ok) {
          router.push("/");
          router.refresh();
          return;
        }
      } catch {
        // ネットワークエラー等は通常のパスワードフォームへフォールバックする。
      }
      if (!cancelled) setCheckingSso(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [router]);

  async function handleLogin(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = (await res.json()) as {
        ok?: boolean;
        challenge?: string;
        session?: string;
        email?: string;
        error?: string;
      };

      if (res.ok && data.challenge === "NEW_PASSWORD_REQUIRED") {
        setPendingEmail(data.email ?? email);
        setPendingSession(data.session ?? "");
        setStage("new_password");
        return;
      }
      if (res.ok) {
        router.push("/");
        router.refresh();
        return;
      }
      setError(
        res.status === 401 ? t("errorInvalidCredentials") : t("errorUnknown"),
      );
    } catch {
      setError(t("errorUnknown"));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleNewPassword(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);

    const form = e.currentTarget;
    const newPassword = (
      form.elements.namedItem("newPassword") as HTMLInputElement
    ).value;
    const confirmPassword = (
      form.elements.namedItem("confirmPassword") as HTMLInputElement
    ).value;

    if (newPassword !== confirmPassword) {
      setError(t("errorPasswordMismatch"));
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: pendingEmail,
          session: pendingSession,
          newPassword,
        }),
      });
      const data = (await res.json()) as { ok?: boolean; error?: string };

      if (res.ok) {
        router.push("/");
        router.refresh();
        return;
      }
      if (data.error === "session_expired" || res.status === 401) {
        setStage("login");
        setPendingSession("");
        setError(t("errorSessionExpired"));
      } else if (data.error === "invalid_password") {
        setError(t("errorInvalidPassword"));
      } else {
        setError(t("errorUnknown"));
      }
    } catch {
      setError(t("errorUnknown"));
    } finally {
      setSubmitting(false);
    }
  }

  if (checkingSso) {
    // silent-login判定中はフォームを表示しない(表示→即消滅のちらつきを防ぐ、
    // prysmのSilentLoginGate.tsxと同じ方針)。ただし何も表示しない(=真っ白なページ)だと
    // ページ自体が固まっている/読み込みに失敗しているように見える(2026-09-09、
    // ユーザー報告を受けたパフォーマンス調査で発見)。AuthShell自体は表示し、
    // フォームの位置にスピナーだけ出す。
    return (
      <AuthShell>
        <div className="flex justify-center py-12">
          <Loader2 className="size-6 animate-spin text-muted" />
        </div>
      </AuthShell>
    );
  }

  if (stage === "new_password") {
    return (
      <AuthShell>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => void handleNewPassword(e)}
        >
          <div>
            <h1 className="mb-2 text-heading">{t("newPasswordTitle")}</h1>
            <p className="text-body text-muted">
              {t("newPasswordDescription")}
            </p>
          </div>
          <div>
            <label
              htmlFor="newPassword"
              className="mb-1 block text-label text-muted"
            >
              {t("newPasswordLabel")}
            </label>
            <input
              id="newPassword"
              name="newPassword"
              type="password"
              required
              minLength={8}
              // biome-ignore lint/a11y/noAutofocus: login form UX
              autoFocus
              autoComplete="new-password"
              className={`w-full ${INPUT_CLASS_MD}`}
            />
          </div>
          <div>
            <label
              htmlFor="confirmPassword"
              className="mb-1 block text-label text-muted"
            >
              {t("confirmPasswordLabel")}
            </label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              className={`w-full ${INPUT_CLASS_MD}`}
            />
          </div>
          {error && <p className="text-error text-label">{error}</p>}
          <Button
            type="submit"
            variant="solid"
            color="primary"
            size="md"
            disabled={submitting}
            loading={submitting}
          >
            {submitting ? t("submitting") : t("newPasswordSubmit")}
          </Button>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <AuthSignInForm
        labels={{
          heading: t("signIn"),
          subtext: t("signInDescription"),
          emailLabel: t("emailLabel"),
          emailPlaceholder: t("emailPlaceholder"),
          passwordLabel: t("passwordLabel"),
          forgotPasswordLabel: t("forgotPasswordLink"),
          submitLabel: t("signInButton"),
          submitLoadingLabel: t("submitting"),
          footerText: t("contactAdmin"),
        }}
        email={email}
        onEmailChange={setEmail}
        password={password}
        onPasswordChange={setPassword}
        onSubmit={handleLogin}
        onForgotPassword={() => router.push("/login/forgot-password")}
        loading={submitting}
        error={
          // AuthSignInForm内部で<p role="alert">に包まれるため、ここで<p>を渡すと
          // <p>の入れ子(無効なHTML)になりhydrationエラーになる。<span>を渡す。
          error ? (
            <span className="text-error text-label">{error}</span>
          ) : undefined
        }
      />
    </AuthShell>
  );
}
